import React, { useEffect, useMemo, useRef, useState } from "react";
import { X, Eye, Paperclip, Bold, Italic, Link as LinkIcon, Mail } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

/**
 * Bulk email composer for admins.
 * - Target: all users / a class year / a preset list of user IDs (from checkbox selection)
 * - Send: via Resend if configured, else falls back to `mailto:` with all recipients in BCC
 * - Body: rich text (contenteditable) with Bold / Italic / Link
 * - Attachment: single file (Resend only — mailto cannot attach)
 * - Preview toggle before sending
 */
export default function BroadcastEmailModal({ open, onClose, selectedUserIds = [] }) {
  const [scope, setScope] = useState(selectedUserIds.length > 0 ? "ids" : "all");
  const [classYear, setClassYear] = useState(2026);
  const [subject, setSubject] = useState("");
  const [preview, setPreview] = useState(false);
  const [recipients, setRecipients] = useState({ count: 0, recipients: [] });
  const editorRef = useRef(null);

  const scopeOptions = useMemo(() => {
    const opts = [
      { value: "all", label: "All users" },
      { value: "class", label: "By class year" },
    ];
    if (selectedUserIds.length > 0) {
      opts.push({ value: "ids", label: `Only selected (${selectedUserIds.length})` });
    }
    return opts;
  }, [selectedUserIds]);

  // fetch recipient count whenever scope changes
  useEffect(() => {
    if (!open) return;
    const payload = { scope };
    if (scope === "class") payload.class_year = classYear;
    if (scope === "ids") payload.user_ids = selectedUserIds;
    api.post("/admin/broadcast/recipients", payload)
      .then(({ data }) => setRecipients(data))
      .catch(() => setRecipients({ count: 0, recipients: [] }));
  }, [open, scope, classYear, selectedUserIds]);

  const applyFormat = (cmd, arg) => {
    editorRef.current?.focus();
    document.execCommand(cmd, false, arg);
  };
  const addLink = () => {
    const url = window.prompt("Link URL (start with https://):", "https://");
    if (url && /^https?:\/\//i.test(url)) applyFormat("createLink", url);
  };

  const getBodyHtml = () => (editorRef.current?.innerHTML || "").trim();

  const sendViaMailto = () => {
    const bodyHtml = getBodyHtml();
    if (!subject.trim()) { toast.error("Subject is required"); return; }
    if (!bodyHtml) { toast.error("Message body is required"); return; }
    // convert HTML to plain text for mailto (basic strip)
    const tmp = document.createElement("div");
    tmp.innerHTML = bodyHtml;
    const plain = tmp.innerText || tmp.textContent || "";
    const bcc = recipients.recipients.map((r) => r.email).join(",");
    const href =
      "mailto:?bcc=" + encodeURIComponent(bcc) +
      "&subject=" + encodeURIComponent(subject.trim()) +
      "&body=" + encodeURIComponent(plain);
    window.location.href = href;
    toast.info(`Opening your mail app with ${recipients.count} recipients in BCC…`);
    onClose?.();
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
      data-testid="broadcast-modal"
      onClick={onClose}
    >
      <div
        className="brutal-card bg-white w-full max-w-3xl my-8"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b-4 border-brand-ink">
          <div className="flex items-center gap-2">
            <Mail className="w-5 h-5" strokeWidth={3} />
            <h2 className="text-xl font-bold text-brand-navy">Email your users</h2>
          </div>
          <button onClick={onClose} data-testid="broadcast-close" className="p-1 hover:bg-brand-cream rounded">
            <X className="w-5 h-5" strokeWidth={3} />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {/* Target */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-bold uppercase text-brand-navy/70 mb-1">Send to</label>
              <select
                value={scope}
                onChange={(e) => setScope(e.target.value)}
                data-testid="broadcast-scope"
                className="brutal-input w-full"
              >
                {scopeOptions.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
            {scope === "class" && (
              <div>
                <label className="block text-xs font-bold uppercase text-brand-navy/70 mb-1">Class year</label>
                <select
                  value={classYear}
                  onChange={(e) => setClassYear(Number(e.target.value))}
                  data-testid="broadcast-class-year"
                  className="brutal-input w-full"
                >
                  {[2026, 2027, 2028].map((y) => (
                    <option key={y} value={y}>Class {y}</option>
                  ))}
                </select>
              </div>
            )}
          </div>
          <div className="text-xs font-bold text-brand-navy/70" data-testid="broadcast-count">
            {recipients.count} recipient{recipients.count === 1 ? "" : "s"} will be added to your email as BCC
          </div>

          {/* Subject */}
          <div>
            <label className="block text-xs font-bold uppercase text-brand-navy/70 mb-1">Subject</label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="A quick note from your teacher"
              data-testid="broadcast-subject"
              className="brutal-input w-full"
            />
          </div>

          {/* Rich text editor */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-xs font-bold uppercase text-brand-navy/70">Message</label>
              <button
                type="button"
                onClick={() => setPreview((p) => !p)}
                data-testid="broadcast-preview-toggle"
                className="text-xs font-bold text-brand-navy hover:underline flex items-center gap-1"
              >
                <Eye className="w-3 h-3" strokeWidth={3} /> {preview ? "Edit" : "Preview"}
              </button>
            </div>
            {!preview && (
              <div className="flex items-center gap-1 mb-2">
                <button type="button" onClick={() => applyFormat("bold")} className="brutal-btn-white text-xs px-2 py-1" title="Bold">
                  <Bold className="w-3 h-3" strokeWidth={3} />
                </button>
                <button type="button" onClick={() => applyFormat("italic")} className="brutal-btn-white text-xs px-2 py-1" title="Italic">
                  <Italic className="w-3 h-3" strokeWidth={3} />
                </button>
                <button type="button" onClick={addLink} className="brutal-btn-white text-xs px-2 py-1" title="Add link">
                  <LinkIcon className="w-3 h-3" strokeWidth={3} />
                </button>
              </div>
            )}
            {preview ? (
              <div
                data-testid="broadcast-preview"
                className="brutal-input w-full min-h-[180px] p-3 prose prose-sm max-w-none"
                dangerouslySetInnerHTML={{ __html: getBodyHtml() || "<em>Nothing to preview yet.</em>" }}
              />
            ) : (
              <div
                ref={editorRef}
                contentEditable
                data-testid="broadcast-body"
                suppressContentEditableWarning
                className="brutal-input w-full min-h-[180px] p-3 focus:outline-none"
                style={{ whiteSpace: "pre-wrap" }}
              />
            )}
          </div>

          {/* Attachment note (mailto can't attach — reminder only) */}
          <div className="text-xs text-brand-navy/60">
            <Paperclip className="inline w-3 h-3 mr-1" strokeWidth={3} />
            Your email app will let you attach files after it opens.
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-end gap-2 p-4 border-t-4 border-brand-ink bg-brand-cream/50">
          <button
            onClick={onClose}
            data-testid="broadcast-cancel"
            className="brutal-btn-white text-sm py-2"
          >
            Cancel
          </button>
          <button
            onClick={sendViaMailto}
            disabled={recipients.count === 0}
            data-testid="broadcast-send-mailto"
            className="brutal-btn-pink text-sm py-2 disabled:opacity-50"
            title="Opens your device's default email app with all recipients in BCC"
          >
            <Mail className="w-3.5 h-3.5" strokeWidth={3} /> Open in mail app
          </button>
        </div>
      </div>
    </div>
  );
}
